/**
 * Centralized Esri / ArcGIS access — every ArcGIS request the frontend makes
 * MUST go through this file. No other module is permitted to construct an
 * arcgis.com URL or instantiate a basemap layer directly.
 *
 * ─── The architecture ───────────────────────────────────────────────────
 *
 * The basemap is delivered by Esri's **Basemap Styles** service, which
 * returns a Mapbox Style Specification JSON document at
 *
 *   https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/
 *     styles/{style}?token=<key>
 *
 * The `arcgis/imagery` style is hybrid: it composes a raster World_Imagery
 * source (from the premium ibasemaps-api.arcgis.com service) with vector
 * reference layers (boundaries, places, roads). One style URL gives us
 * satellite + crisp vector labels in a single rendered map.
 *
 * The token attached to the style URL transitively authenticates every
 * derived tile / sprite / glyph request — Esri rewrites the URLs in the
 * style document with the same token. One key, one auth surface.
 *
 * ─── Why MapLibre, not Leaflet TileLayer ────────────────────────────────
 *
 * The metered raster Static Basemap Tiles catalog does NOT publish a
 * satellite raster style — `arcgis/imagery/labels` is labels-only. To get
 * raster satellite imagery under a paid/metered SKU, we have to consume the
 * Basemap Styles service, which is vector. MapLibre is the renderer.
 *
 * We use `@maplibre/maplibre-gl-leaflet`, the canonical Leaflet plugin
 * (maintained under the MapLibre GitHub org), which wraps the MapLibre
 * canvas as a single Leaflet layer. Every Leaflet overlay primitive
 * (Polygon, Marker, CircleMarker, GeoJSON, Rectangle, Tooltip, Popup,
 * custom panes) keeps working unchanged — we did NOT migrate off
 * react-leaflet.
 *
 * ─── Required API-key privilege ─────────────────────────────────────────
 *
 *   Basemap styles service        ← required for the basemap
 *   Geocode (not stored)          ← required if the address search bar is used
 *
 * Set referrer restrictions on the key in the Esri Developer console
 * (argostellus.com, dev.argostellus.com, localhost:3000). The key is baked
 * into the bundle and is visible in DevTools — the referrer restriction is
 * the real protection.
 *
 * ─── Verifying you're on the paid service ───────────────────────────────
 *
 * 1. DevTools → Network → filter `arcgis`. You should see requests to
 *    basemapstyles-api.arcgis.com (style document), ibasemaps-api.arcgis.com
 *    (raster imagery tiles), basemaps-api.arcgis.com (vector font glyphs),
 *    cdn.arcgis.com (sprites). All with `token=...` populated.
 * 2. ArcGIS Developer dashboard → Usage. Basemap styles, Map tiles, and
 *    Vector tiles requests will appear within ~5–15 min of first traffic.
 * 3. The geocoder host (geocode.arcgis.com) is metered and shows usage as
 *    soon as the address search bar is exercised.
 */

import { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
// Type-only — pulled in at the type layer, erased at build time. The runtime
// `maplibre-gl` payload (~800KB) is loaded dynamically inside <EsriVectorBasemap />
// so pages without a map don't pay the parse/eval cost.
import type maplibregl from 'maplibre-gl';

const RAW_KEY = (import.meta.env.VITE_ARCGIS_API_KEY ?? '').trim();

const BASEMAP_STYLES_HOST =
  'https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/styles';

/** Append `?token=…` (or `&token=…`) to an Esri URL when a key is set. */
function withToken(url: string): string {
  if (!RAW_KEY) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(RAW_KEY)}`;
}

/** Standard attribution. Esri's ToS requires visible credit. */
export const ESRI_ATTRIBUTION = 'Tiles &copy; Esri';

/** Soil Survey overlay attribution (USDA-NRCS via Esri). */
export const SOIL_ATTRIBUTION = 'USDA NRCS Soil Survey (Esri)';

// ── Basemap style URL ───────────────────────────────────────────────────

/**
 * URL of the Esri vector basemap style. Default is `arcgis/imagery` — hybrid
 * raster satellite + vector reference labels — which is what every Argos
 * map uses. Pass a different style key for variations.
 */
export function esriImageryStyleUrl(style: string = 'arcgis/imagery'): string {
  return withToken(`${BASEMAP_STYLES_HOST}/${style}`);
}

// ── <EsriVectorBasemap /> ────────────────────────────────────────────────
//
// Render as a child of <MapContainer> (react-leaflet). It attaches a
// MapLibre-rendered vector basemap to the surrounding Leaflet map and
// removes it on unmount. Returns null — it's a side-effect component.
//
// Attribution is intentionally set to false on the plugin; Leaflet's own
// attribution control owns that UI surface for consistency with the rest
// of Argos's maps, so callers should pass `attribution={ESRI_ATTRIBUTION}`
// to a sibling Leaflet layer or set it via `<MapContainer attributionControl>`.

declare module 'leaflet' {
  // The plugin attaches itself to the Leaflet namespace at import time, but
  // it ships no TypeScript declarations. Type the surface we touch.
  function maplibreGL(options: {
    style: string | object;
    attribution?: string | false;
    interactive?: boolean;
    pane?: string;
  }): L.Layer;
}

/**
 * Allowlist of attribution strings that may appear in a Leaflet attribution
 * control attached to an Argos map. Anything outside this list (the verbose
 * multi-vendor manifest the maplibre-gl-leaflet plugin pulls from Esri's
 * style document, the random "Leaflet" prefix, etc.) gets silently dropped.
 * Minimum legal credit per provider:
 *   - Tiles © Esri        — required for Esri basemap content
 *   - USDA NRCS Soil ...  — only present when the soil overlay is enabled
 */
const ATTRIBUTION_ALLOWLIST: ReadonlySet<string> = new Set([
  ESRI_ATTRIBUTION,
  SOIL_ATTRIBUTION,
]);

export function EsriVectorBasemap(): null {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    let cancelled = false;
    let layer: L.Layer | null = null;
    let restoreAddAttribution: (() => void) | null = null;

    // Install the attribution allowlist FIRST, before the MapLibre layer
    // mounts. The plugin reads source-level attributions from Esri's style
    // and calls ctrl.addAttribution(...) for each. By intercepting at the
    // insertion point we keep the control to exactly the allowlist no matter
    // what the plugin tries to push.
    const ctrl = map.attributionControl;
    if (ctrl) {
      ctrl.setPrefix(false);
      // Strip anything already present that isn't on the allowlist.
      const existing = Object.keys(
        (ctrl as unknown as { _attributions?: Record<string, number> })
          ._attributions ?? {},
      );
      for (const k of existing) {
        if (!ATTRIBUTION_ALLOWLIST.has(k)) ctrl.removeAttribution(k);
      }
      // Patch addAttribution to filter all future insertions.
      const origAdd = ctrl.addAttribution.bind(ctrl);
      (ctrl as unknown as { addAttribution: (s: string) => L.Control })
        .addAttribution = (text: string) =>
          ATTRIBUTION_ALLOWLIST.has(text) ? origAdd(text) : ctrl;
      restoreAddAttribution = () => {
        (ctrl as unknown as { addAttribution: typeof origAdd })
          .addAttribution = origAdd;
      };
      // Always show our basemap credit while this component is mounted.
      ctrl.addAttribution(ESRI_ATTRIBUTION);
    }

    (async () => {
      // Dynamic imports — defers the ~800KB maplibre-gl payload until a map
      // mounts. The plugin's import is side-effect-only (it augments the
      // global L namespace with L.maplibreGL).
      await Promise.all([
        import('maplibre-gl'),
        import('@maplibre/maplibre-gl-leaflet'),
      ]);
      if (cancelled) return;

      // Fetch the style document so we can cap the raster source's maxzoom.
      // Esri's style declares maxzoom: 23 on the World_Imagery raster source,
      // but Esri only reliably publishes imagery through LOD 19 worldwide —
      // requests for zoom 20+ return 404 in most areas, flooding the console
      // and producing blank tiles when users zoom in close. Capping the
      // source's maxzoom at 19 makes MapLibre over-zoom (scale up the deepest
      // available tile) instead of fetching tiles that don't exist.
      let style: string | object = esriImageryStyleUrl();
      try {
        const resp = await fetch(esriImageryStyleUrl());
        if (resp.ok) {
          const styleObj = await resp.json();
          const sources = styleObj?.sources ?? {};
          for (const src of Object.values(sources) as Array<{
            type?: string;
            maxzoom?: number;
          }>) {
            if (src?.type === 'raster') src.maxzoom = 19;
          }
          style = styleObj;
        }
      } catch {
        // Fall back to URL-only mode if the fetch fails — MapLibre will
        // handle it the same way, just with 404s past zoom 19.
      }
      if (cancelled) return;

      layer = L.maplibreGL({
        style,
        attribution: false,
        // Leaflet drives all interaction (pan, zoom, draw, click); MapLibre
        // is render-only. Without this flag the MapLibre canvas intercepts
        // pointer events before they reach Leaflet's useMapEvents handlers
        // (breaks RectangleDraw in PhreaticCommand, click-to-place wells,
        // tooltips, etc.).
        interactive: false,
      });
      layer.addTo(map);
    })();

    return () => {
      cancelled = true;
      if (layer) map.removeLayer(layer);
      restoreAddAttribution?.();
      ctrl?.removeAttribution(ESRI_ATTRIBUTION);
    };
  }, [map]);

  return null;
}

// ── Soil Survey overlay ────────────────────────────────────────────────
//
// USDA-NRCS publishes the Soil Survey tile cache through Esri's legacy
// `server.arcgisonline.com` host. This is USDA government content rather
// than Esri commercial content. Leaving it on the legacy host is an
// intentional, scoped exception. Revisit only if USDA changes hosting or
// Esri publishes a metered equivalent.

/** USDA NRCS Soil Survey overlay (via Esri tile cache — legacy host, USDA content). */
export function soilSurveyTileUrl(): string {
  return withToken(
    'https://server.arcgisonline.com/ArcGIS/rest/services/Specialty/Soil_Survey_Map/MapServer/tile/{z}/{y}/{x}',
  );
}

// ── Geocoder ───────────────────────────────────────────────────────────
//
// `geocode.arcgis.com` is a metered host on the Location Platform; the
// existing key authenticates it.
//
// IMPORTANT — Display-Locator only:
// The Esri geocoder allows results to be used for display only. If a caller
// ever wants to persist the returned lat/lon to the database, that crosses
// into "Storage Locator" territory (separate paid SKU). Keep results in
// transient UI state only.

export interface GeocodeCandidate {
  name: string;
  lat: number;
  lon: number;
  city?: string;
  region?: string;
}

export interface GeocodeOptions {
  /** Bounding box `xmin,ymin,xmax,ymax` in WGS84. Defaults to TX/NM/OK box. */
  searchExtent?: string;
  maxLocations?: number;
}

/**
 * Forward-geocode an address. Results are display-only — do not persist.
 */
export async function geocodeAddress(
  query: string,
  options: GeocodeOptions = {},
): Promise<GeocodeCandidate[]> {
  const q = encodeURIComponent(query.trim());
  if (!q) return [];
  const params = new URLSearchParams({
    f: 'json',
    singleLine: query.trim(),
    outFields: 'City,Region',
    maxLocations: String(options.maxLocations ?? 5),
    searchExtent: options.searchExtent ?? '-109,25,-93,38',
  });
  const url = withToken(
    `https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/findAddressCandidates?${params.toString()}`,
  );
  const resp = await fetch(url);
  if (!resp.ok) return [];
  const data = await resp.json();
  return (data.candidates ?? []).map((c: any): GeocodeCandidate => ({
    name: c.address,
    lat: c.location.y,
    lon: c.location.x,
    city: c.attributes?.City,
    region: c.attributes?.Region,
  }));
}
